import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Link2, Printer, Mail, Phone, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { BRAND, whatsappLink } from "@/lib/brand";
import WhatsAppIcon from "@/components/WhatsAppIcon";

/**
 * Reusable legal document page template.
 * Props:
 *   pageKey: which translations key holds {eyebrow,title,intro,sections}
 */
export default function LegalLayout({ pageKey }) {
  const { t } = useLanguage();
  const page = t[pageKey];
  const [copiedId, setCopiedId] = useState(null);

  const copySectionLink = async (id) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch (e) {
      // noop
    }
  };

  return (
    <div className="bg-white" data-testid={`legal-page-${pageKey}`}>
      {/* Hero strip */}
      <section
        className="grain-texture"
        style={{ background: "var(--brand-primary)", color: "#fff" }}
      >
        <div className="container-brand py-14 md:py-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-display font-medium opacity-90 hover:opacity-100 transition-opacity"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} /> {t.common.backToHome}
          </Link>
          <p
            className="mt-7 text-[11px] font-display font-semibold uppercase tracking-[0.18em] inline-block px-3 py-1 rounded-full"
            style={{ background: "var(--brand-accent)", color: "#000" }}
          >
            {page.eyebrow}
          </p>
          <h1
            className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            style={{ color: "#fff" }}
          >
            {page.title}
          </h1>
          <p className="mt-5 max-w-3xl text-base sm:text-lg opacity-90 leading-relaxed">
            {page.intro}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
            <span className="opacity-80">
              {t.common.lastUpdated}: <strong className="opacity-100">{t.common.lastUpdatedDate}</strong>
            </span>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="print-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-display font-semibold transition-colors"
              style={{ background: "rgba(255,255,255,0.12)", color: "#fff" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.12)")}
            >
              <Printer size={14} /> {t.common.printDocument}
            </button>
          </div>
        </div>
      </section>

      {/* Two-column reading area */}
      <section className="container-brand py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <p className="font-display font-semibold text-xs uppercase tracking-[0.18em] text-[color:var(--brand-primary)] mb-3">
                {t.common.tableOfContents}
              </p>
              <nav aria-label="Document sections" className="flex flex-col gap-1">
                {page.sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="text-sm py-1.5 px-2 rounded-md transition-colors text-[#26324a] hover:bg-[color:var(--brand-surface-soft)] hover:text-[color:var(--brand-primary)]"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>

              <div
                className="mt-8 rounded-2xl p-5 border"
                style={{ borderColor: "var(--brand-border-soft)", background: "var(--brand-surface-soft)" }}
              >
                <p className="font-display font-semibold text-sm text-[color:var(--brand-primary)]">
                  {t.common.contactBrandlab}
                </p>
                <a
                  href={whatsappLink(t.whatsapp.message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="legal-whatsapp-cta"
                  className="mt-3 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-display font-semibold text-white transition-colors w-full justify-center"
                  style={{ background: "var(--brand-primary)" }}
                >
                  <WhatsAppIcon size={14} /> {t.whatsapp.cta}
                </a>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="mt-3 flex items-center gap-2 text-sm text-[color:var(--brand-primary)] hover:underline underline-offset-4"
                >
                  <Mail size={14} /> {BRAND.email}
                </a>
                <a
                  href={`tel:${BRAND.phoneHref}`}
                  className="mt-2 flex items-center gap-2 text-sm text-[color:var(--brand-primary)] hover:underline underline-offset-4"
                >
                  <Phone size={14} /> {BRAND.phone}
                </a>
              </div>
            </div>
          </aside>

          <article className="lg:col-span-9">
            <div className="legal-prose">
              {page.sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-28">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <h2>{s.title}</h2>
                    <button
                      type="button"
                      onClick={() => copySectionLink(s.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-display font-medium px-2.5 py-1 rounded-full border border-brand-soft text-[color:var(--brand-primary)] hover:bg-[color:var(--brand-surface-soft)] transition-colors"
                      data-testid={`copy-link-${s.id}`}
                      aria-label={t.common.copyLink}
                    >
                      {copiedId === s.id ? (
                        <>
                          <Check size={12} /> {t.common.copied}
                        </>
                      ) : (
                        <>
                          <Link2 size={12} /> {t.common.copyLink}
                        </>
                      )}
                    </button>
                  </div>
                  <p>{s.body}</p>
                </section>
              ))}

              <div
                className="mt-12 p-6 rounded-2xl border"
                style={{ borderColor: "var(--brand-border-soft)", background: "var(--brand-surface-soft)" }}
              >
                <p className="font-display font-semibold text-[color:var(--brand-primary)] m-0">
                  {t.common.contactBrandlab}
                </p>
                <p className="m-0 mt-2 text-sm">
                  <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a> ·{" "}
                  <a href={`tel:${BRAND.phoneHref}`}>{BRAND.phone}</a>
                </p>
                <a
                  href={whatsappLink(t.whatsapp.message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="legal-whatsapp-cta-bottom"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-display font-semibold text-white transition-all hover:-translate-y-[1px] no-underline"
                  style={{ background: "var(--brand-primary)" }}
                >
                  <WhatsAppIcon size={14} /> {t.whatsapp.cta}
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
